import { isEmpty } from 'bold-ui'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { emptyArrayOf, partition } from 'util/array'

import { CONSULTA_SEGUIMENTO_PERGUNTAS } from '../consultaSeguimentoPerguntasMap'
import { ConsultaSeguimentoPerguntaStatusEnum, PassouFalhouEnum } from '../enums-consultaSeguimento'
import {
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoAplicacaoStatsModel,
  ConsultaSeguimentoFieldModel,
  MChatPerguntaPrincipal,
} from '../model-consultaSeguimento'

const EMPTY_APLICACAO = {}

/**
 * Cria a aplicação com status inicial para as perguntas selecionadas
 */
export function createInitialPerguntasStatus(
  perguntasSelecionadas: Readonly<MChatPerguntaEnum[]> = emptyArrayOf<MChatPerguntaEnum>()
): ConsultaSeguimentoAplicacaoModel {
  if (isEmpty(perguntasSelecionadas)) return EMPTY_APLICACAO

  return Object.fromEntries(
    perguntasSelecionadas.map((pergunta) => [pergunta, { status: ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL }])
  ) as ConsultaSeguimentoAplicacaoModel
}

/**
 * Inicializa a primeira pergunta com status RESPONDENDO,
 * ou se já houver dado com status RESPONDENDO_FALHOU ou RESPONDENDO_PASSOU
 */
export function initializeFirstPerguntaStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel | undefined | null
): ConsultaSeguimentoAplicacaoModel {
  if (!aplicacao || isEmpty(aplicacao)) return EMPTY_APLICACAO

  const perguntas = Object.keys(aplicacao) as MChatPerguntaPrincipal[]
  const firstPergunta = perguntas[0]

  if (!firstPergunta) return aplicacao

  const firstPerguntaData = aplicacao[firstPergunta]

  const updated = Object.fromEntries(
    perguntas.map((pergunta) => {
      const item = aplicacao[pergunta]
      const isStatusRespondendoOuDisponivel =
        item?.status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO ||
        item?.status === ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL

      const newStatus = isStatusRespondendoOuDisponivel
        ? ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
        : item?.status

      return [pergunta, { ...item, status: newStatus }]
    })
  )

  const isFirstPerguntaResponding = firstPerguntaData && isPerguntaStatusRespondendo(firstPerguntaData.status)

  updated[firstPergunta] = {
    ...updated[firstPergunta],
    status: isFirstPerguntaResponding ? firstPerguntaData?.status : ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
  }

  return updated
}

/**
 * Inicializa valores da aplicação mesclando perguntas antigas e novas
 */
export function initializeAplicacaoValues(
  perguntasSelecionadas: Readonly<MChatPerguntaPrincipal[]> = emptyArrayOf<MChatPerguntaPrincipal>(),
  previousAplicacao: ConsultaSeguimentoAplicacaoModel = EMPTY_APLICACAO
): ConsultaSeguimentoAplicacaoModel {
  const sortedPerguntas = [...perguntasSelecionadas].sort((a, b) => a.localeCompare(b))

  if (isEmpty(previousAplicacao)) {
    const initialStatus = createInitialPerguntasStatus(sortedPerguntas)
    return initializeFirstPerguntaStatus(initialStatus)
  }

  const existingPerguntas = new Set(Object.keys(previousAplicacao))
  const newPerguntas = sortedPerguntas.filter((p) => !existingPerguntas.has(p))
  const newAplicacao = createInitialPerguntasStatus(newPerguntas)

  const mergedAplicacao = Object.fromEntries(
    sortedPerguntas.map((pergunta) => [pergunta, previousAplicacao[pergunta] ?? newAplicacao[pergunta]])
  )

  return initializeFirstPerguntaStatus(mergedAplicacao)
}

/**
 * Verifica se o status é alguma variante de RESPONDENDO
 */
export function isPerguntaStatusRespondendo(
  status: ConsultaSeguimentoPerguntaStatusEnum | undefined
): status is
  | ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO
  | ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU
  | ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU {
  if (!status) return false
  return [
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU,
  ].includes(status)
}

/**
 * Verifica se uma pergunta específica possui respostas (dados além de status e resultado)
 */
export function hasPerguntaData(
  pergunta: MChatPerguntaPrincipal,
  aplicacao: ConsultaSeguimentoAplicacaoModel
): boolean {
  const perguntaData = aplicacao[pergunta]
  if (!perguntaData) return false

  const { status, resultado, ...otherValues } = perguntaData
  return !isEmpty(otherValues)
}

/**
 * Verifica se alguma pergunta na aplicação possui respostas (dados além de status e resultado)
 */
export function hasAnyPerguntaData(aplicacao: ConsultaSeguimentoAplicacaoModel | undefined | null): boolean {
  if (!aplicacao || isEmpty(aplicacao)) return false

  return (Object.keys(aplicacao) as MChatPerguntaPrincipal[]).some((item) => {
    return hasPerguntaData(item, aplicacao)
  })
}

/**
 * Retorna informações sobre a pergunta ativa (com status RESPONDENDO, RESPONDENDO_FALHOU ou RESPONDENDO_PASSOU)
 */
export function getPerguntaAtivaInfo(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): {
  perguntaAtiva: MChatPerguntaPrincipal | null
  index: number
  isFirst: boolean
  isLast: boolean
} {
  const perguntas: MChatPerguntaPrincipal[] = Object.keys(aplicacao ?? {}) as MChatPerguntaPrincipal[]
  const perguntaAtiva = perguntas.find((pergunta) => isPerguntaStatusRespondendo(aplicacao[pergunta]?.status)) ?? null
  const index = perguntaAtiva ? perguntas.indexOf(perguntaAtiva) : -1
  const isFirst = index === 0
  const isLast = index === perguntas.length - 1

  return { perguntaAtiva, index, isFirst, isLast }
}

/**
 * Retorna o próximo status da pergunta na aplicação
 */
export function getNextPerguntaStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum | undefined
): ConsultaSeguimentoPerguntaStatusEnum | null {
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  return null
}

/**
 * Retorna o status anterior da pergunta na aplicação
 */
export function getPreviousPerguntaStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum | undefined
): ConsultaSeguimentoPerguntaStatusEnum {
  if (
    status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO ||
    status === ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  ) {
    return ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU
  }
  return ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
}

/**
 * Atualiza a última pergunta para o status anterior
 */
export function setLastPerguntaToPreviousStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoAplicacaoModel {
  const perguntas = Object.keys(aplicacao) as MChatPerguntaPrincipal[]
  const lastPergunta = perguntas[perguntas.length - 1]

  return {
    ...aplicacao,
    [lastPergunta]: {
      ...aplicacao[lastPergunta],
      status: getPreviousPerguntaStatus(aplicacao[lastPergunta]?.status),
    },
  }
}

/**
 * Finaliza o status da última pergunta, transitando de `RESPONDENDO_*` para o respectivo `FINALIZADO_*`.
 *
 * - `RESPONDENDO_FALHOU` → `FINALIZADO_FALHOU`
 * - `RESPONDENDO_PASSOU` → `FINALIZADO_PASSOU`
 *
 * Retorna a aplicação sem modificações se não houver última pergunta
 * ou se o status atual não permitir transição.
 */
export function setLastPerguntaToNextStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel | undefined
): ConsultaSeguimentoAplicacaoModel {
  const perguntas = aplicacao ? (Object.keys(aplicacao) as MChatPerguntaPrincipal[]) : []
  const lastPergunta = perguntas[perguntas.length - 1]

  if (!lastPergunta) return { ...aplicacao }

  const nextStatus = getNextPerguntaStatus(aplicacao?.[lastPergunta]?.status)

  if (!nextStatus) return { ...aplicacao }

  return {
    ...aplicacao,
    [lastPergunta]: {
      ...aplicacao?.[lastPergunta],
      status: nextStatus,
    },
  }
}

/**
 * Finaliza o status da pergunta ativa, transitando de `RESPONDENDO_*` para o respectivo `FINALIZADO_*`.
 *
 * - `RESPONDENDO_FALHOU` → `FINALIZADO_FALHOU`
 * - `RESPONDENDO_PASSOU` → `FINALIZADO_PASSOU`
 *
 * Retorna a aplicação sem modificações se não houver pergunta ativa
 * ou se o status atual não permitir transição.
 */
export function setPerguntaAtivaToNextStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel | undefined | null
): ConsultaSeguimentoAplicacaoModel {
  if (!aplicacao || isEmpty(aplicacao)) return EMPTY_APLICACAO

  const perguntaAtiva = getPerguntaAtivaInfo(aplicacao)?.perguntaAtiva
  if (!perguntaAtiva) return aplicacao

  const nextStatus = getNextPerguntaStatus(aplicacao[perguntaAtiva]?.status)
  if (!nextStatus) return aplicacao

  return {
    ...aplicacao,
    [perguntaAtiva]: {
      ...aplicacao[perguntaAtiva],
      status: nextStatus,
    },
  }
}

/**
 * Retorna lista de perguntas que possuem dados além de status e resultado
 */
export function getPerguntasWithData(
  aplicacao: ConsultaSeguimentoAplicacaoModel | undefined | null
): MChatPerguntaPrincipal[] {
  if (!aplicacao || isEmpty(aplicacao)) return []

  return (Object.keys(aplicacao) as MChatPerguntaPrincipal[]).filter((item) => {
    return hasPerguntaData(item, aplicacao)
  })
}

export function getAplicacaoStats(aplicacao: ConsultaSeguimentoAplicacaoModel): ConsultaSeguimentoAplicacaoStatsModel {
  const perguntasRespondidas = Object.keys(aplicacao) as MChatPerguntaPrincipal[]

  const [perguntasPassou, perguntasFalhou] = partition(
    perguntasRespondidas,
    (pergunta) => aplicacao?.[pergunta]?.resultado === PassouFalhouEnum.PASSOU
  )

  const totalPerguntasRespondidas = perguntasRespondidas.length
  const totalPerguntasPassou = perguntasPassou.length
  const totalPerguntasFalhou = perguntasFalhou.length

  return {
    totalPerguntasFalhou,
    totalPerguntasPassou,
    totalPerguntasRespondidas,
  }
}

/**
 * Remove todos os ramos/caminhos não utilizados do cache, mantendo apenas
 * a trajetória final escolhida pelo usuário que levou ao resultado do questionário
 */
export function pruneAplicacao(aplicacao: ConsultaSeguimentoAplicacaoModel): ConsultaSeguimentoAplicacaoModel {
  const resultado: ConsultaSeguimentoAplicacaoModel = {}
  const perguntasKeys = Object.keys(aplicacao) as MChatPerguntaPrincipal[]

  for (const perguntaKey of perguntasKeys) {
    const respostas = aplicacao[perguntaKey]

    if (respostas) {
      const { pruneUnusedBranches } = CONSULTA_SEGUIMENTO_PERGUNTAS[perguntaKey]

      resultado[perguntaKey] = pruneUnusedBranches(respostas)
    }
  }

  return resultado
}

export function getAplicacaoInitialValues(
  valueInCache: ConsultaSeguimentoFieldModel | undefined,
  mChatPerguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>,
  isConsultaSeguimentoDesatualizada: boolean
): {
  aplicacao: ConsultaSeguimentoAplicacaoModel
  perguntasSelecionadas: ReadonlyArray<MChatPerguntaPrincipal>
} {
  const hasValueInCache = valueInCache && !isEmpty(valueInCache)

  if (!hasValueInCache) {
    return {
      aplicacao: {},
      perguntasSelecionadas: mChatPerguntasFalhas,
    }
  }

  if (isConsultaSeguimentoDesatualizada) {
    const perguntasSelecionadas = [...valueInCache.perguntasSelecionadas, ...mChatPerguntasFalhas].removeDuplicate()
    return {
      aplicacao: initializeAplicacaoValues(perguntasSelecionadas, valueInCache.aplicacao),
      perguntasSelecionadas,
    }
  }

  return {
    aplicacao: initializeFirstPerguntaStatus(valueInCache.aplicacao),
    perguntasSelecionadas: valueInCache.perguntasSelecionadas,
  }
}

export function isConsultaSeguimentoDesatualizada(
  valueInCache: ConsultaSeguimentoFieldModel | undefined,
  mChatPerguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>
): boolean {
  const hasValueInCache = valueInCache && !isEmpty(valueInCache)

  if (!hasValueInCache) {
    return false
  }

  return !valueInCache.perguntasSelecionadas.containsAll(mChatPerguntasFalhas)
}
